import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import HelmetWrapper from 'components/HelmetWrapper';
import classNames from 'classnames';
import Subnav from 'components/Subnav';
import HomeIcon from 'icons/Home.icon';
import ReportsIcon from 'icons/Reports.icon';
import TableIcon from 'icons/Table.icon';
import DownloadIcon from 'icons/Download.icon';
import { Button } from 'ncoded-component-library';
import utils, { getDataRoomRoute } from 'utils';
import useQueryParams from 'hooks/useQueryParams';
import { ReportFilterType } from './pages/Reports/types';
import dataRoomApi from '../../dataRoomApi';
import { NEW_WINDOW_PROPS } from 'data/global';
import {
  ANALYTICS_SELECTOR,
  MOBILE_ANALYTICS_SELECTOR,
} from '../../providers/Onboarding/data';

import './Reports.styles.scss';

const prepareUrl = (href: string) =>
  utils.downloadUrl({
    href,
    ...NEW_WINDOW_PROPS,
  });

const ReportsRouter: React.FC = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    doesntHaveAccess,
    hasIndexGenerated,
    isAdmin,
    dataRoom: { _id, name },
  } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const {
    params: { action, memberRole, dataRoomMemberId, from, to },
  } = useQueryParams<ReportFilterType>();

  const { t } = useTranslation();

  const isOnOverview = useLocation().pathname.includes('overview');

  const classes = classNames('reports', 'page');

  const links = useMemo(
    () => [
      {
        to: 'overview',
        label: t('overview'),
        Icon: HomeIcon,
      },
      {
        to: 'activity-log',
        label: t('activityLog'),
        id: ANALYTICS_SELECTOR,
        Icon: ReportsIcon,
      },
    ],
    [t],
  );

  const params = useMemo(
    () => ({
      action,
      memberRole,
      dataRoomMemberId,
      createdAt: from || to ? { $gte: from, $lte: to } : null,
    }),
    [action, dataRoomMemberId, from, memberRole, to],
  );

  const downloadIndexFile = useCallback(async () => {
    try {
      setIsDownloading(true);

      const {
        data: { url },
      } = await dataRoomApi.room.getExportedIndex(_id);

      if (url) {
        prepareUrl(url);
      }
    } catch (e) {
      popServerError(e);
    } finally {
      setIsDownloading(false);
    }
  }, [_id, popServerError]);

  const downloadPDF = useCallback(async () => {
    try {
      setIsDownloading(true);

      const {
        data: { url },
      } = await dataRoomApi.reports.getPdfReport(_id, params);

      if (url) {
        prepareUrl(url);
      }
    } catch (e) {
      popServerError(e);
    } finally {
      setIsDownloading(false);
    }
  }, [_id, params, popServerError]);

  const downloadCSV = useCallback(async () => {
    try {
      setIsDownloading(true);

      const {
        data: { url },
      } = await dataRoomApi.reports.getCsvReport(_id, params);

      if (url) {
        prepareUrl(url);
      }
    } catch (e) {
      popServerError(e);
    } finally {
      setIsDownloading(false);
    }
  }, [_id, params, popServerError]);

  const RenderExportIndex = useMemo(
    () =>
      isOnOverview ? (
        hasIndexGenerated ? (
          <>
            <p>{t('export')}</p>
            <Button
              icon={DownloadIcon}
              onClick={downloadIndexFile}
              disabled={isDownloading}
            >
              {t('fileIndex')}
            </Button>
          </>
        ) : null
      ) : (
        <>
          <p>{t('export')}</p>
          <Button
            icon={<ReportsIcon strokeWidth={2} />}
            onClick={downloadPDF}
            disabled={isDownloading}
          >
            {t('logPdf')}
          </Button>
          <Button
            icon={TableIcon}
            onClick={downloadCSV}
            disabled={isDownloading}
          >
            {t('logCsv')}
          </Button>
        </>
      ),
    [
      hasIndexGenerated,
      isDownloading,
      isOnOverview,
      downloadCSV,
      downloadIndexFile,
      downloadPDF,
      t,
    ],
  );

  if (!isAdmin)
    return <Navigate to={getDataRoomRoute(_id, 'documents')} replace />;

  return (
    <div className={classes}>
      <HelmetWrapper title={`${t('analytics&Insights')} - ${name}`} />
      <h2 id={MOBILE_ANALYTICS_SELECTOR}>{t('analytics&Insights')}</h2>
      <div className="reports__heading">
        <Subnav links={links} />
        {!doesntHaveAccess && <div>{RenderExportIndex}</div>}
      </div>
      <Outlet />
    </div>
  );
};

export default ReportsRouter;
